import { Globe, MoreHorizontal } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { isHeadquartersLocation } from "@/lib/locations";
import { useLingui } from "@lingui/react/macro";
import { Link } from "@tanstack/react-router";
import type { Location } from "@/types/location";

export interface LocationItemProps {
  location: Location;
  /** Tile variant passes the parent org name as an extra meta row. */
  organizationName?: string;
  /** When true the name + "View" link to the location detail (tile variant). */
  asLink?: boolean;
}

/**
 * Single location row. The shared core behind LocationCard (plain) and
 * LocationTile (linked, with optional organization row).
 */
export function LocationItem({
  location,
  organizationName,
  asLink = false,
}: LocationItemProps) {
  const { t } = useLingui();
  const linkParams = { locationId: String(location.id) };

  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-lg border border-(--color-border) bg-(--color-surface-muted) p-3",
        asLink && "w-full",
      )}
    >
      <div className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-(--color-border) bg-(--color-surface-content)">
        <Globe className="size-5 text-(--color-text-secondary)" />
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex items-center gap-2">
          {asLink ? (
            <Link
              to="/locations/$locationId"
              params={linkParams}
              className="whitespace-nowrap text-sm font-medium text-(--color-text-primary) hover:underline"
            >
              {location.name}
            </Link>
          ) : (
            <span className="whitespace-nowrap text-sm font-medium text-(--color-text-primary)">
              {location.name}
            </span>
          )}
          <Badge
            className={cn(
              "h-6 rounded-full border-transparent px-2 py-0.5 text-xs font-medium",
              location.isActive
                ? "bg-(--color-status-active) text-(--brand-white) hover:bg-(--color-status-active)/90"
                : "bg-(--color-surface-accent) text-(--color-text-secondary) hover:bg-(--color-surface-accent)",
            )}
          >
            {location.isActive ? t`Active` : t`Inactive`}
          </Badge>
          {isHeadquartersLocation(location) && (
            <Badge
              variant="secondary"
              className="h-6 rounded-full px-2 py-0.5 text-xs font-medium"
              title={t`Created automatically from the concept's address`}
            >
              {t`HQ`}
            </Badge>
          )}
        </div>
        <span className="truncate text-xs text-(--color-text-secondary)">
          {[location.code, location.city, location.timezone]
            .filter(Boolean)
            .join(" • ")}
        </span>
        {organizationName ? (
          <span className="truncate text-xs text-(--color-text-secondary)">
            {t`Organization`}: {organizationName}
          </span>
        ) : null}
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex size-13 shrink-0 items-center justify-center rounded-[10px] border border-(--color-border) bg-(--color-surface-content) transition-colors hover:bg-(--color-surface-muted)"
          >
            <MoreHorizontal className="size-5 text-(--color-text-secondary)" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {asLink ? (
            <DropdownMenuItem asChild>
              <Link to="/locations/$locationId" params={linkParams}>
                {t`View`}
              </Link>
            </DropdownMenuItem>
          ) : (
            <>
              <DropdownMenuItem>{t`View`}</DropdownMenuItem>
              <DropdownMenuItem>{t`Edit`}</DropdownMenuItem>
              <DropdownMenuItem className="text-(--color-danger-text)">
                {t`Deactivate`}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
